/**
 * @fileoverview
 *
 * Hook resolving proper appContext based on a type.
 * Channel config is loaded here via useChannel hook.
 *
 * TODO(CS-1468): Refactor useEmbedAppContext hook to update the state instead of returning the value
 */

import { useMemo } from 'react'

import { useAdConfigStorage } from '@ads/hooks/useAdConfigStorage'
import { useChannel } from '@core/channel'
import type { FeedSourceConfig } from '@core/feed'
import { uuidv4 } from '@core/helpers/uuidv4'
import { getGuestId } from '@core/tracking'
import { useSetBusinessId } from '@core/tracking/funnelTracking/hooks/useSetBusinessId'
import type { IAnyWidgetParams } from '@core/types'
import { useStore } from '@core/widgets/store'
import { paramsSelector } from '@core/widgets/store/selectors'
import type { AppContextType } from '@embed/components/helpers'
import { use50PercentTraffic } from '@embed/hooks/subscriptionFlags/use50PercentTraffic'
import { use95PercentTraffic } from '@embed/hooks/subscriptionFlags/use95PercentTraffic'

import { getAppContext } from './getAppContext'
import type { IWidgetAppContext } from './types'

/**
 * Hook returning appContext. Fetches channel and
 * ad config data if needed.
 */
export const useEmbedAppContext = <TParams extends IAnyWidgetParams>({
  config,
  appContextType,
  extraParams,
}: {
  config: FeedSourceConfig | null
  appContextType: AppContextType
  extraParams?: Partial<IAnyWidgetParams>
}): IWidgetAppContext<TParams> | null => {
  const storeParams = useStore(paramsSelector) as TParams

  const params = useMemo(() => {
    return {
      ...storeParams,
      ...extraParams,
    }
  }, [extraParams, storeParams])

  const channel = useChannel({
    username: params.channel,
    encodedId: params.channel_id,
    target: params.target,
  })

  use50PercentTraffic(channel)
  use95PercentTraffic(channel)
  useSetBusinessId(channel?.business_id)

  const adConfig = useAdConfigStorage(params)
  const embedInstanceId = useMemo(() => uuidv4(), [])

  return useMemo(() => {
    if (!config) {
      return null
    }
    const guestId = getGuestId()
    const appContext = getAppContext<TParams>({
      embedFeedConfig: config,
      appContextType,
      channel,
      embedInstanceId,
      params,
      guestId,
      adConfig,
    })

    return appContext
  }, [config, appContextType, channel, embedInstanceId, params, adConfig])
}
